// 広告を消すサブスクの状態（entitlements/{uid}）。書くのは関数（stripeWebhook）だけで、一覧は読むだけ。
// 広告なしのあいだは、全画面と帯の広告を出さず、ごほうびの広告は見ずにごほうびを渡す（ads/index.ts）。
import { doc, onSnapshot, type Timestamp, type Unsubscribe } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { onSession } from "./auth";
import { config } from "./config";
import { db, functions } from "./firebase";
import { getLocale } from "./i18n";

export interface Entitlement {
  adFree: boolean;
  /** 次の更新（または終わり）の時刻 */
  periodEnd: Date | null;
  /** 期間の終わりで解約される予約がある */
  cancelAtPeriodEnd: boolean;
}

const NONE: Entitlement = { adFree: false, periodEnd: null, cancelAtPeriodEnd: false };
let state: Entitlement = NONE;
const listeners = new Set<(e: Entitlement) => void>();
let unsub: Unsubscribe | null = null;
let currentUid: string | null = null;

function emit(next: Entitlement) {
  state = next;
  document.body.classList.toggle("ad-free", next.adFree);
  listeners.forEach((fn) => fn(state));
}

export const entitlement = () => state;
export const isAdFree = () => state.adFree;

export function onEntitlement(fn: (e: Entitlement) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** ログインの状態に合わせて、本人の entitlements を見張る */
export function startEntitlement() {
  if (!config.billingEnabled) return;
  onSession((s) => {
    const uid = s.user?.uid ?? null;
    if (uid === currentUid) return;
    currentUid = uid;
    unsub?.();
    unsub = null;
    emit(NONE);
    if (!uid) return;
    unsub = onSnapshot(
      doc(db, "entitlements", uid),
      (snap) => {
        if (!snap.exists()) return emit(NONE);
        const end = snap.get("periodEnd") as Timestamp | null | undefined;
        emit({ adFree: snap.get("adFree") === true, periodEnd: end ? end.toDate() : null, cancelAtPeriodEnd: snap.get("cancelAtPeriodEnd") === true });
      },
      (e) => console.error("[entitlement]", e),
    );
  });
}

/** Stripe の支払いの画面へ移る。consent：18歳以上か、保護者の同意を得ている */
export async function startCheckout(consent: boolean): Promise<void> {
  const r = await httpsCallable<unknown, { url: string }>(functions, "createCheckout")({ consent, locale: getLocale() });
  location.assign(r.data.url);
}

/** 支払いの管理・解約の画面へ移る */
export async function openPortal(): Promise<void> {
  const r = await httpsCallable<unknown, { url: string }>(functions, "createPortal")({});
  location.assign(r.data.url);
}
